import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useDebounced } from "../lib/media";
import { ticketIdFromTerm } from "../lib/ticket-id";
import { cn } from "./ui/cn";
import { SearchIcon } from "./ui/icons";

/**
 * The one box for finding a ticket you already know something about.
 *
 * It sits in the sidebar, beside the notification bell and the new-ticket
 * button, because it answers a question an agent has anywhere in the app --
 * including on the dashboard, which has no ticket list of its own to search.
 * Putting it inside the tickets page meant the answer to "where is that order"
 * was only reachable from the screen you had already navigated away from.
 *
 * It is not a filter. The filters narrow the queue on screen; this abandons the
 * queue and looks across the whole desk, closed tickets included.
 *
 * "/" focuses it, as it does in most things with a search box, so finding an
 * order reference somebody has just read out never needs the mouse.
 */
export function TicketSearch({
  value,
  onChange,
  onSubmit,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Enter, with what is in the box -- undebounced, unlike `onChange`'s trip
      through the URL. */
  onSubmit?: (value: string) => void;
  className?: string;
}) {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      // Not while writing: "/" is a character, and stealing it mid-reply to
      // move the caret out of the composer would be worse than no shortcut.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }

      /*
       * Only once the box is actually on screen. The desktop sidebar stays
       * mounted under `display: none` on a phone, where search lives behind
       * the hamburger instead -- and `isConnected` cannot tell the difference,
       * so it would strand the keyboard in an invisible input. An element
       * hidden that way has no offsetParent.
       */
      if (!input.current?.offsetParent) return;

      event.preventDefault();
      input.current.focus();
      input.current.select();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={cn("relative", className)}>
      <SearchIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />

      <input
        ref={input}
        // `search` rather than `text` so the browser offers its own history and
        // a native clear affordance on the platforms that have one.
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && onSubmit) onSubmit(event.currentTarget.value);
        }}
        placeholder="Search"
        aria-label="Search tickets"
        className={cn(
          "h-8 w-full rounded-md border pl-7 pr-7 text-sm text-content",
          "placeholder:text-faint",
          // WebKit draws its own cancel button, which would sit on top of ours.
          "[&::-webkit-search-cancel-button]:appearance-none",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          value
            ? "border-brand-500 bg-brand-50 dark:bg-brand-950/40"
            : "border-line bg-surface",
        )}
      />

      {/*
        Our own clear button: the native one is absent in Firefox, styled
        differently everywhere it does exist, and unreachable by keyboard in
        Safari -- so the way out of a search would depend on the browser.
      */}
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange("");
            input.current?.focus();
          }}
          aria-label="Clear search"
          title="Clear search"
          className={cn(
            "absolute right-1 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center",
            "rounded text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        >
          &times;
        </button>
      ) : null}
    </div>
  );
}

/**
 * Search wired to the URL, for the sidebar.
 *
 * The query lives in `?q=` rather than in component state because the box now
 * outlives the page under it: an agent can start typing on the dashboard and
 * has to end up looking at results. Putting it in the URL also makes a search
 * shareable and survives the back button, and leaves exactly one source of
 * truth for the tickets page to read.
 */
export function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const urlQuery = params.get("q") ?? "";

  const [value, setValue] = useState(urlQuery);
  const debounced = useDebounced(value, 250);

  /*
   * What the URL last agreed with, so the two directions below cannot fight.
   * Without it, the round trip through the URL lands back on the box mid-word
   * and overwrites whatever was typed while the navigation was in flight.
   */
  const settled = useRef(urlQuery);

  // URL -> box: the back button, or a link that arrived carrying a query.
  useEffect(() => {
    if (urlQuery === settled.current) return;
    settled.current = urlQuery;
    setValue(urlQuery);
  }, [urlQuery]);

  /*
   * The last debounced value this box acted on, so that only typing publishes.
   *
   * The effect below also re-runs whenever the location moves, and there the
   * debounced value is stale: the URL -> box effect has just moved `settled`
   * to the new query, but `debounced` still holds the old one for another
   * 250ms. Comparing the two then reads as the agent having typed the old
   * query, and navigates back to it. The sidebar is mounted twice -- the
   * desktop column and the phone drawer, which stays mounted while hidden --
   * so one box's search moved the URL, the other box put it back, and the
   * first box answered: the URL flipped between the two on every keypress.
   */
  const acted = useRef(debounced);

  // Box -> URL.
  useEffect(() => {
    if (debounced === acted.current) return;
    acted.current = debounced;

    const trimmed = debounced.trim();
    if (trimmed === settled.current) return;
    settled.current = trimmed;

    const next = new URLSearchParams(location.search);
    if (trimmed) next.set("q", trimmed);
    else next.delete("q");

    /*
     * Searching from a page with no ticket list has to land somewhere that has
     * one. That is a real navigation and keeps its history entry, so the back
     * button returns to the dashboard; refining a search already on a queue
     * replaces instead, so one search does not bury the previous page under a
     * dozen history entries.
     */
    const onTickets = location.pathname.startsWith("/v/");

    navigate(
      { pathname: onTickets ? location.pathname : "/v/open", search: next.toString() },
      { replace: onTickets },
    );
  }, [debounced, location.pathname, location.search, navigate]);

  /*
   * Enter on "#1234" opens ticket 1234.
   *
   * Typing it already finds the ticket and lists it first (the API ranks the
   * id above text matches), but an agent holding a ticket number wants the
   * ticket, not a one-row list to click through. Only with the `#` -- see
   * `ticketIdFromTerm` -- because a bare number is as often an order.
   *
   * The search stays in the URL, so the list beside the ticket is the search
   * that found it and the box still says what was asked. Opened in All open,
   * which can show a ticket of any status, as every other link to a ticket
   * is. A real navigation rather than a replace: the agent can go back to
   * where they were.
   */
  const submit = (raw: string): void => {
    const id = ticketIdFromTerm(raw);
    if (id === null) return;

    const trimmed = raw.trim();
    acted.current = raw;
    settled.current = trimmed;

    const next = new URLSearchParams(location.search);
    next.set("q", trimmed);
    navigate({ pathname: `/v/open/${id}`, search: next.toString() });
  };

  return (
    <TicketSearch
      value={value}
      onChange={setValue}
      onSubmit={submit}
      className={className}
    />
  );
}
